import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart2,
  Dumbbell,
  LayoutDashboard,
  Lightbulb,
  MessageCircle,
  RefreshCw,
  TrendingUp,
  User,
  UtensilsCrossed,
} from 'lucide-react';
import { getSpotlightRegions, getElementHighlightRect, getSpotlightLabelStyle, getSpotlightPanelPlacement, getAdjacentPanelPlacement, getTopCenteredPanelPlacement, getBottomCenteredPanelPlacement, getSidePanelPlacement, findTourElement, SPOTLIGHT_PADDING } from './tourElementUtils';
import TourMascot from './TourMascot';

const CARD_WIDTH = 'min(92vw, 500px)';
// Width of welcome video card = width of a 16:9 frame at max height min(48vh, 360px).
const WELCOME_VIDEO_WIDTH = 'min(92vw, min(640px, calc(min(48vh, 360px) * 16 / 9)))';
const REGION_SLOT_COUNT = 4;
const MISCLICK_EVENT = 'portfolio-playbook-misclick';
const SKIP_ARM_MS = 5000;

const SECTION_ICONS = {
  dashboard: LayoutDashboard,
  client: User,
  chat: MessageCircle,
  nutrition: UtensilsCrossed,
  analytics: BarChart2,
  training: Dumbbell,
  grow: TrendingUp,
};

const signalMisclick = () => {
  window.dispatchEvent(new CustomEvent(MISCLICK_EVENT));
};

const applyRingBox = (el, rect, padding = SPOTLIGHT_PADDING) => {
  if (!el) return;
  if (!rect) {
    el.style.display = 'none';
    return;
  }
  el.style.display = '';
  el.style.top = `${rect.top - padding}px`;
  el.style.left = `${rect.left - padding}px`;
  el.style.width = `${rect.width + padding * 2}px`;
  el.style.height = `${rect.height + padding * 2}px`;
};

const applyRegionBox = (el, region) => {
  if (!el) return;
  if (!region) {
    el.style.display = 'none';
    return;
  }
  el.style.display = '';
  el.style.top = typeof region.top === 'number' ? `${region.top}px` : region.top;
  el.style.left = typeof region.left === 'number' ? `${region.left}px` : region.left;
  el.style.width = typeof region.width === 'number' ? `${region.width}px` : region.width;
  el.style.height = typeof region.height === 'number' ? `${region.height}px` : region.height;
};

const applyStyleMap = (el, style) => {
  if (!el) return;
  if (!style) {
    el.style.display = 'none';
    return;
  }
  el.style.display = '';
  el.style.top = style.top ?? '';
  el.style.left = style.left ?? '';
  el.style.right = style.right ?? '';
  el.style.bottom = style.bottom ?? '';
  el.style.transform = style.transform ?? '';
  el.style.width = style.width ?? '';
  el.style.maxHeight = style.maxHeight ?? '';
};

const TourOverlay = ({
  showWelcome,
  welcomeStage = 'gate',
  waitingForDashboard = false,
  step,
  stepIndex,
  totalSteps,
  highlightedElement,
  secondaryHighlightedElement,
  targetMissing = false,
  tooltipKey,
  language,
  direction,
  isDarkMode,
  welcome,
  demoClientName,
  onStart,
  onSkipWelcome,
  onNext,
  onPrev,
  onSkip,
  onSkipSection,
  canSkipSection = false,
  onToggleLanguage,
  onInteractiveActivate,
  volume = 1,
  onVolumeChange,
}) => {
  const isHebrew = language === 'hebrew';
  const cardBg = isDarkMode ? 'bg-surface' : 'bg-surface';
  const textPrimary = isDarkMode ? 'text-white' : 'text-ink';
  const textSecondary = isDarkMode ? 'text-ink-muted' : 'text-ink-muted';
  const textMuted = isDarkMode ? 'text-ink-muted' : 'text-ink-muted';
  const [misclickPulse, setMisclickPulse] = useState(0);
  const [skipArmed, setSkipArmed] = useState(false);
  const skipArmTimer = useRef(null);
  const misclickClearTimer = useRef(null);

  useEffect(() => {
    const onMisclick = () => {
      setMisclickPulse((n) => n + 1);
      // Orange highlight is temporary and scoped to this step only.
      if (misclickClearTimer.current) clearTimeout(misclickClearTimer.current);
      misclickClearTimer.current = setTimeout(() => {
        setMisclickPulse(0);
        misclickClearTimer.current = null;
      }, 1200);
    };
    window.addEventListener(MISCLICK_EVENT, onMisclick);
    return () => window.removeEventListener(MISCLICK_EVENT, onMisclick);
  }, []);

  useEffect(() => {
    setSkipArmed(false);
    setMisclickPulse(0);
    if (skipArmTimer.current) {
      clearTimeout(skipArmTimer.current);
      skipArmTimer.current = null;
    }
    if (misclickClearTimer.current) {
      clearTimeout(misclickClearTimer.current);
      misclickClearTimer.current = null;
    }
  }, [stepIndex, showWelcome]);

  useEffect(() => () => {
    if (skipArmTimer.current) clearTimeout(skipArmTimer.current);
    if (misclickClearTimer.current) clearTimeout(misclickClearTimer.current);
  }, []);

  const fill = (text) => {
    if (!text) return text;
    const name = demoClientName || (isHebrew ? 'הלקוח' : 'your client');
    return text.replace(/\{name\}/g, name);
  };

  // Captions: Title Case every word (English only). Strip em / double dashes.
  const captionize = (text) => {
    if (!text) return text;
    const cleaned = String(text)
      .replace(/\s*--+\s*/g, ' ')
      .replace(/\s*[—–]+\s*/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    if (isHebrew) return cleaned;
    return cleaned.replace(/[A-Za-z0-9']+/g, (word) => (
      word.charAt(0).toUpperCase() + word.slice(1)
    ));
  };

  // Body copy: no dash asides; each sentence on its own line.
  const bodyLines = (text) => {
    if (!text) return [];
    const cleaned = String(text)
      .replace(/\s*--+\s*/g, '. ')
      .replace(/\s*[—–]+\s*/g, '. ')
      .replace(/\s{2,}/g, ' ')
      .replace(/\.\s*\./g, '.')
      .trim();
    const parts = cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleaned];
    return parts.map((s) => s.trim()).filter(Boolean);
  };

  const renderBody = (text, className) => {
    const lines = bodyLines(text);
    if (lines.length === 0) return null;
    if (lines.length === 1) {
      return <p className={className}>{lines[0]}</p>;
    }
    return (
      <div className={`${className} space-y-1.5`}>
        {lines.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    );
  };

  const isSpotlight = !showWelcome && step?.mode === 'spotlight' && highlightedElement;
  const stepInteractive = !showWelcome && step?.mode === 'spotlight' && step?.interactive === true;
  const useHitProxy = stepInteractive && !step?.waitForEvent && !step?.scrollFriendly;
  const isInteractive = isSpotlight && step?.interactive === true;
  const softSpotlight = isSpotlight && step?.softSpotlight === true;
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024
  );
  useEffect(() => {
    const syncMobile = () => setIsMobileLayout(window.innerWidth < 1024);
    syncMobile();
    window.addEventListener('resize', syncMobile);
    return () => window.removeEventListener('resize', syncMobile);
  }, []);
  const clickLabel = stepInteractive
    ? fill((isMobileLayout && step?.clickLabelMobile) ? step.clickLabelMobile : step?.clickLabel)
    : null;

  // Animate the ring only when it moves to a NEW target, not on every scroll
  // reposition. Otherwise the transition makes the ring lag/chase while scrolling.
  const [animateRing, setAnimateRing] = useState(false);
  const prevHighlightRef = useRef(null);
  useEffect(() => {
    if (highlightedElement && highlightedElement !== prevHighlightRef.current) {
      prevHighlightRef.current = highlightedElement;
      setAnimateRing(true);
      const timer = setTimeout(() => setAnimateRing(false), 350);
      return () => clearTimeout(timer);
    }
    if (!highlightedElement) prevHighlightRef.current = null;
    return undefined;
  }, [highlightedElement]);
  const ringTransition = animateRing ? 'transition-all duration-300' : '';

  // Only interactive steps let clicks reach the page (intended control / hit proxy).
  // Brief steps keep the dim blocking — capture-phase also blocks spotlight holes
  // so in-page buttons stay inert and nudge "nothing to click here".
  const passThroughClicks = stepInteractive;

  const spotlightLabel =
    step?.spotlightLabel
      ? fill(step.spotlightLabel)
      : (isInteractive && clickLabel ? clickLabel : null);

  const isFloatStory =
    !showWelcome
    && !waitingForDashboard
    && step?.mode === 'story'
    && step?.layout === 'float';

  const showMissingTargetFallback =
    !showWelcome
    && !waitingForDashboard
    && targetMissing
    && step?.mode === 'spotlight'
    && !highlightedElement;

  const isCentered =
    showWelcome
    || waitingForDashboard
    || showMissingTargetFallback
    || (step?.mode === 'story' && !isFloatStory)
    || step?.mode === 'transition';

  const isFullDim = isCentered || isFloatStory;

  // Imperative spotlight geometry — updated on scroll/resize without React
  // re-renders so the glow stays locked to the target (e.g. table row).
  // (Ping/pulse ring removed — static glow only.)
  const regionRefs = useRef([]);
  const ringRef = useRef(null);
  const secondaryRingRef = useRef(null);
  const labelRef = useRef(null);
  const panelRef = useRef(null);
  const hitProxyRef = useRef(null);

  const resolvePanelPlacement = useCallback((primaryRect) => {
    if (showWelcome || !isSpotlight) return null;
    const panelPos = step?.panelPosition || step?.position || 'top';
    const panelHeight = step?.panelHeight || (step?.panelCompact ? 150 : 200);
    if (step?.panelDock === 'center') {
      return {
        layout: 'center',
        style: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: CARD_WIDTH },
      };
    }
    if (step?.panelDock === 'bottom') return getBottomCenteredPanelPlacement({ maxWidth: 560 });
    if (step?.panelDock === 'top') return getTopCenteredPanelPlacement({ maxWidth: 560 });
    if (step?.panelDock === 'side') {
      return getSidePanelPlacement(primaryRect, {
        isRtl: isHebrew,
        panelWidth: step?.panelCompact ? 300 : 340,
      });
    }
    if (step?.panelAnchor && (panelPos === 'left' || panelPos === 'right')) {
      const anchorEl = findTourElement(step.panelAnchor, { isMobile: isMobileLayout });
      const panelAnchorRect = anchorEl ? getElementHighlightRect(anchorEl) : null;
      if (panelAnchorRect) {
        return getAdjacentPanelPlacement(panelAnchorRect, panelPos, { panelHeight });
      }
    }
    return getSpotlightPanelPlacement(primaryRect, { preferred: panelPos, panelHeight });
  }, [showWelcome, isSpotlight, step, isMobileLayout, isHebrew]);

  // Always resolve the interactive control's live rect (via highlight or a direct
  // data-tour lookup) so the click proxy can sit over it even before the ring
  // highlight has committed.
  const resolveProxyRect = useCallback(() => {
    if (!stepInteractive) return null;
    const clickId = step?.clickTarget
      ? ((isMobileLayout && step.clickTargetMobile) ? step.clickTargetMobile : step.clickTarget)
      : null;
    if (clickId) {
      const clickEl = findTourElement(clickId, { isMobile: isMobileLayout });
      if (clickEl) return getElementHighlightRect(clickEl);
    }
    let el = highlightedElement && highlightedElement.isConnected ? highlightedElement : null;
    if (!el) {
      const target = (isMobileLayout && step?.targetMobile) ? step.targetMobile : step?.target;
      if (target) el = findTourElement(target, { isMobile: isMobileLayout });
    }
    return el ? getElementHighlightRect(el) : null;
  }, [stepInteractive, highlightedElement, isMobileLayout, step]);

  const syncSpotlightGeometry = useCallback(() => {
    if (isFullDim) {
      applyRegionBox(regionRefs.current[0], { top: 0, left: 0, width: '100%', height: '100%' });
      for (let i = 1; i < REGION_SLOT_COUNT; i += 1) applyRegionBox(regionRefs.current[i], null);
      applyRingBox(ringRef.current, null);
      applyRingBox(secondaryRingRef.current, null);
      applyRingBox(hitProxyRef.current, null);
      applyStyleMap(labelRef.current, null);
      return;
    }

    if (!isSpotlight) {
      // No committed highlight yet — still keep the click proxy live over the
      // real control so an interactive step is never un-clickable.
      if (useHitProxy) {
        applyRingBox(hitProxyRef.current, resolveProxyRect(), Math.max(SPOTLIGHT_PADDING, 14));
      } else {
        applyRingBox(hitProxyRef.current, null);
      }
      return;
    }

    const primaryRect = highlightedElement ? getElementHighlightRect(highlightedElement) : null;
    const secondaryRect = secondaryHighlightedElement
      ? getElementHighlightRect(secondaryHighlightedElement)
      : null;
    // Prefer the larger secondary cutout only when it actually contains the primary
    // (e.g. menu shell around an item). Otherwise a mismatched secondary paints a
    // hole nowhere near the ring.
    const secondaryContainsPrimary = !!(
      secondaryRect
      && primaryRect
      && primaryRect.left >= secondaryRect.left - 2
      && primaryRect.top >= secondaryRect.top - 2
      && primaryRect.right <= secondaryRect.right + 2
      && primaryRect.bottom <= secondaryRect.bottom + 2
    );
    const dimRect = secondaryContainsPrimary ? secondaryRect : primaryRect;
    const regions = getSpotlightRegions(
      dimRect,
      softSpotlight ? Math.max(SPOTLIGHT_PADDING, 14) : SPOTLIGHT_PADDING,
    );
    for (let i = 0; i < REGION_SLOT_COUNT; i += 1) {
      applyRegionBox(regionRefs.current[i], regions[i] || null);
    }

    applyRingBox(ringRef.current, primaryRect, softSpotlight ? 10 : SPOTLIGHT_PADDING);
    applyRingBox(secondaryRingRef.current, secondaryContainsPrimary ? secondaryRect : null);

    // Invisible hit target above the panel/dim so interactive clicks always reach
    // the tour activate handler (e.g. open Maya), even when the card overlaps it.
    // Prefer clickTarget rect when the visual highlight is a larger shell (menu).
    if (useHitProxy) {
      applyRingBox(hitProxyRef.current, resolveProxyRect() || primaryRect, Math.max(SPOTLIGHT_PADDING, 14));
    } else {
      applyRingBox(hitProxyRef.current, null);
    }

    const labelRect = resolveProxyRect() || primaryRect;
    if (spotlightLabel && labelRect) {
      applyStyleMap(
        labelRef.current,
        getSpotlightLabelStyle(labelRect, {
          placement: step?.spotlightLabelPlacement,
          isRtl: isHebrew,
        }),
      );
    } else {
      applyStyleMap(labelRef.current, null);
    }

    const placement = resolvePanelPlacement(primaryRect);
    applyStyleMap(panelRef.current, placement?.style || null);
  }, [
    isFullDim,
    isSpotlight,
    isInteractive,
    stepInteractive,
    useHitProxy,
    softSpotlight,
    resolveProxyRect,
    highlightedElement,
    secondaryHighlightedElement,
    spotlightLabel,
    step,
    isHebrew,
    resolvePanelPlacement,
  ]);

  useLayoutEffect(() => {
    syncSpotlightGeometry();
    // Re-measure after paint so the ring matches post-layout / post-highlight size
    // (Personal Details bar was undersized until the next scroll bumped sync).
    const raf = window.requestAnimationFrame(() => {
      syncSpotlightGeometry();
      window.requestAnimationFrame(syncSpotlightGeometry);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [syncSpotlightGeometry, tooltipKey]);

  useEffect(() => {
    if (showWelcome || (!isSpotlight && !isFullDim && !stepInteractive)) return undefined;
    let raf = 0;
    const bump = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        syncSpotlightGeometry();
      });
    };
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, { passive: true, capture: true });
    let poll;
    if (isSpotlight || (stepInteractive && !isSpotlight)) {
      let ticks = 0;
      poll = window.setInterval(() => {
        syncSpotlightGeometry();
        ticks += 1;
        if (ticks >= 12) {
          window.clearInterval(poll);
          poll = null;
        }
      }, 100);
    }
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, { capture: true });
      if (raf) window.cancelAnimationFrame(raf);
      if (poll) window.clearInterval(poll);
    };
  }, [showWelcome, isSpotlight, isFullDim, stepInteractive, syncSpotlightGeometry]);

  // Snapshot for first paint / non-scroll layout (panel classNames).
  const panelPlacement = useMemo(() => {
    if (showWelcome || !isSpotlight || !highlightedElement) return null;
    return resolvePanelPlacement(getElementHighlightRect(highlightedElement));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWelcome, isSpotlight, highlightedElement, secondaryHighlightedElement, step, isMobileLayout, tooltipKey, resolvePanelPlacement]);

  const skeletonBlock = isDarkMode ? 'bg-moss' : 'bg-moss';

  const dashboardSkeletonCard = (
    <div
      className={`${cardBg} rounded-lg  border-2 border-forest/30 relative overflow-hidden pointer-events-none`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="p-4 sm:p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg shrink-0 animate-pulse ${skeletonBlock}`} />
          <div className="flex-1 space-y-2">
            <div className={`h-3.5 w-2/5 rounded animate-pulse ${skeletonBlock}`} />
            <div className={`h-2.5 w-3/5 rounded animate-pulse ${skeletonBlock}`} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`h-14 sm:h-16 rounded-lg animate-pulse ${skeletonBlock}`} />
          ))}
        </div>
        <div className={`h-24 sm:h-28 rounded-lg animate-pulse ${skeletonBlock}`} />
        <div className="space-y-2 pt-1">
          <div className={`h-2.5 w-full rounded animate-pulse ${skeletonBlock}`} />
          <div className={`h-2.5 w-4/5 rounded animate-pulse ${skeletonBlock}`} />
        </div>
        <p className={`text-center text-xs font-medium ${textMuted} pt-1`}>
          {isHebrew ? 'טוען את לוח הבקרה...' : 'Loading your command center...'}
        </p>
      </div>
    </div>
  );

  const isLastStep = stepIndex >= totalSteps - 1;
  const isWelcomeGate = showWelcome && welcomeStage !== 'video';
  const isWelcomeVideo = showWelcome && welcomeStage === 'video';
  // `videoId: null` marks steps whose narrated clip no longer matches the UI.
  const mascotStepId = isWelcomeVideo
    ? 'welcome'
    : (step && 'videoId' in step ? step.videoId : step?.id);
  const title = showWelcome
    ? captionize(isWelcomeVideo
      ? (welcome?.videoTitle || welcome?.title)
      : welcome?.title)
    : captionize(fill(step?.title));
  const description = showWelcome
    ? null
    : fill(step?.description);
  const welcomeParagraphs = showWelcome
    ? (isWelcomeVideo
      ? (welcome?.videoParagraphs || welcome?.paragraphs || [])
      : (welcome?.paragraphs || []))
        .flatMap((p) => bodyLines(p))
    : [];
  const welcomeCta = isWelcomeGate
    ? captionize(welcome?.gateCta || (isHebrew ? 'התחלת הסיור' : 'Start Tour'))
    : showWelcome
      ? captionize(welcome?.cta || (isHebrew ? 'המשך' : 'Continue'))
      : null;
  const proTip = showWelcome ? null : fill(step?.proTip);
  const nextLabel = showWelcome
    ? null
    : (isLastStep
      ? (isHebrew ? 'סיום' : 'Finish')
      : captionize(fill(step?.nextLabel) || (isHebrew ? 'הבא' : 'Next')));

  const stepHint = isInteractive && clickLabel ? (
    <>
      <p
        key={`hint-${misclickPulse}`}
        className={`mt-1 text-[10px] sm:text-xs font-semibold text-forest-ondark dark:text-forest ${
          misclickPulse ? 'playbook-misclick-hint' : ''
        }`}
      >
        → {clickLabel}
      </p>
      {step?.allowManualAdvance && (
        <p className="mt-0.5 text-[10px] sm:text-xs font-medium text-ink-muted dark:text-ink-muted">
          {isHebrew ? 'חסום? המשיכו עם הבא.' : "Can't click it? Press Next to continue."}
        </p>
      )}
    </>
  ) : !showWelcome && !isInteractive ? (
    <p
      key={`hint-${misclickPulse}`}
      className={`mt-1.5 text-[10px] sm:text-xs font-semibold text-forest-ondark dark:text-forest ${
        misclickPulse ? 'playbook-misclick-hint' : ''
      }`}
    >
      {showMissingTargetFallback
        ? (isHebrew ? 'האלמנט לא נמצא — המשיכו עם הבא' : 'Target not found — press Next to continue')
        : (bodyLines(fill(step?.continueHint) || (isHebrew ? 'אין צורך ללחוץ כאן. המשיכו עם הבא' : 'Nothing to click here. Press Next to continue')).join(' '))}
    </p>
  ) : null;

  const subtleBtnInteractive = isDarkMode
    ? 'bg-moss text-ink hover:bg-moss active:bg-moss'
    : 'bg-moss text-ink hover:bg-moss active:bg-line';

  const canGoBack = !showWelcome;
  const skipHandler = showWelcome ? onSkipWelcome : onSkip;
  const primaryHandler = showWelcome ? onStart : onNext;
  const primaryLabel = showWelcome ? welcomeCta : nextLabel;

  const handleSkipClick = () => {
    if (skipArmed) {
      if (skipArmTimer.current) {
        clearTimeout(skipArmTimer.current);
        skipArmTimer.current = null;
      }
      setSkipArmed(false);
      skipHandler?.();
      return;
    }
    setSkipArmed(true);
    if (skipArmTimer.current) clearTimeout(skipArmTimer.current);
    skipArmTimer.current = setTimeout(() => {
      setSkipArmed(false);
      skipArmTimer.current = null;
    }, SKIP_ARM_MS);
  };

  const cancelSkipArm = () => {
    if (skipArmTimer.current) {
      clearTimeout(skipArmTimer.current);
      skipArmTimer.current = null;
    }
    setSkipArmed(false);
  };

  const progressPct = totalSteps > 0
    ? Math.round(((stepIndex + 1) / totalSteps) * 100)
    : 0;

  const primaryBtnClass =
    'inline-flex items-center justify-center rounded-lg bg-forest-ondark text-white dark:text-canvas-dark text-sm font-semibold ' +
    '  ' +
    'hover:bg-forest-ondark/90   hover:-translate-y-px ' +
    'active:bg-forest-ondark active:translate-y-0  ' +
    'transition-[background-color,box-shadow,transform] duration-150 ease-out cursor-pointer';

  const bottomHotBar = (
    <div className="pointer-events-auto fixed inset-x-0 bottom-0 z-[10004] px-3 sm:px-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2">
      <div
        className={`mx-auto max-w-3xl flex items-center gap-2 sm:gap-3 rounded-lg border border-forest/30  px-3 py-2.5 sm:px-4 sm:py-3 ${
          isDarkMode ? 'bg-canvas/95' : 'bg-white/95'
        } ${isHebrew ? 'flex-row-reverse' : ''}`}
      >
        <button
          type="button"
          onClick={() => {
            cancelSkipArm();
            onPrev?.();
          }}
          disabled={!canGoBack}
          className={`min-w-[4.5rem] px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
            canGoBack
              ? `${subtleBtnInteractive} cursor-pointer`
              : 'bg-moss/60 text-ink-muted cursor-not-allowed dark:bg-surface dark:text-ink-muted'
          }`}
        >
          {isHebrew ? 'הקודם' : 'Back'}
        </button>

        <button
          type="button"
          onClick={onToggleLanguage}
          className={`min-w-[2.75rem] px-2.5 py-2 rounded-lg text-xs sm:text-sm font-semibold border border-forest/30 transition-colors cursor-pointer ${subtleBtnInteractive}`}
          title={isHebrew ? 'Switch to English' : 'עברית'}
          aria-label={isHebrew ? 'Switch to English' : 'עברית'}
        >
          {isHebrew ? 'EN' : 'ע'}
        </button>

        <span
          className={`w-px h-6 shrink-0 ${isDarkMode ? 'bg-moss' : 'bg-moss'}`}
          aria-hidden
        />

        {!showWelcome && (
          <div className="flex-1 min-w-0 px-1">
            <div className="w-full bg-line/50 dark:bg-moss/60 rounded-full h-2 overflow-hidden relative">
              <div
                className="playbook-liquid-fill h-full rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className={`text-[10px] ${textMuted} mt-0.5 text-center tabular-nums`}>
              {progressPct}%
            </p>
          </div>
        )}

        {showWelcome && <div className="flex-1" />}

        {skipArmed ? (
          <div className={`flex items-center gap-1.5 shrink-0 flex-wrap justify-end ${isHebrew ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={cancelSkipArm}
              className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isDarkMode
                  ? 'text-ink-muted hover:text-white hover:bg-surface'
                  : 'text-ink-muted hover:text-ink hover:bg-moss'
              }`}
            >
              {isHebrew ? 'המשך סיור' : 'Keep touring'}
            </button>
            {!showWelcome && canSkipSection && (
              <button
                type="button"
                onClick={() => {
                  cancelSkipArm();
                  onSkipSection?.();
                }}
                className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  isDarkMode
                    ? 'bg-progress/20 text-progress ring-1 ring-progress/50'
                    : 'bg-progress-faint text-progress ring-1 ring-progress/60'
                }`}
              >
                {isHebrew ? 'דלג על הפרק' : 'Skip section'}
              </button>
            )}
            <button
              type="button"
              onClick={handleSkipClick}
              className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                isDarkMode
                  ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/50'
                  : 'bg-amber-50 text-amber-700 ring-1 ring-amber-400/60'
              }`}
            >
              {showWelcome
                ? (isHebrew ? 'אשר דילוג' : 'Confirm skip')
                : (isHebrew ? 'סיים סיור' : 'End tour')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleSkipClick}
            className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isDarkMode
                ? 'text-ink-muted hover:text-white hover:bg-surface'
                : 'text-ink-muted hover:text-ink hover:bg-moss'
            }`}
          >
            {isHebrew ? 'דלג' : 'Skip'}
          </button>
        )}

        {!showWelcome && (
          <button
            type="button"
            onClick={() => {
              cancelSkipArm();
              primaryHandler?.();
            }}
            className={`${primaryBtnClass} min-w-[5.5rem] px-4 py-2 sm:px-5 text-xs sm:text-sm`}
          >
            {primaryLabel}
          </button>
        )}
      </div>
    </div>
  );

  const isSectionCard = !showWelcome && step?.sectionCard === true;
  const SectionIcon = isSectionCard ? (SECTION_ICONS[step?.icon] || LayoutDashboard) : null;
  const sectionProgressLabel = isSectionCard && step?.sectionIndex && step?.sectionTotal
    ? (isHebrew
      ? `פרק ${step.sectionIndex} מתוך ${step.sectionTotal}`
      : `Section ${step.sectionIndex} of ${step.sectionTotal}`)
    : null;

  const centeredCard = isWelcomeVideo ? (
    <div
      className={`${cardBg} rounded-lg  relative pointer-events-auto flex flex-col max-h-[90vh] overflow-hidden border ${
        isDarkMode ? 'border-line' : 'border-line'
      }`}
    >
      <div className="p-3 sm:p-3.5 flex flex-col gap-3">
        <TourMascot
          stepId={mascotStepId}
          language={language}
          label={title}
          volume={volume}
          onVolumeChange={onVolumeChange}
          variant="welcome"
        />
        <div className={`flex flex-col gap-3 ${isHebrew ? 'text-right' : 'text-left'}`}>
          <div className="min-w-0">
            <h2 className={`text-lg sm:text-xl font-semibold tracking-tight ${textPrimary} leading-snug`}>
              {title}
            </h2>
            <div className={`mt-2 space-y-2 text-sm ${textSecondary} leading-relaxed`}>
              {welcomeParagraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              cancelSkipArm();
              primaryHandler?.();
            }}
            className={`${primaryBtnClass} w-full px-5 py-2.5`}
          >
            {welcomeCta || (isHebrew ? 'המשך' : 'Continue')}
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div
      className={`${cardBg} rounded-lg  relative pointer-events-auto flex flex-col max-h-[90vh] overflow-hidden border ${
        isDarkMode ? 'border-line' : 'border-line'
      }`}
    >
      <div className={`flex flex-col gap-3 p-5 sm:p-6 ${isHebrew ? 'text-right' : 'text-left'}`}>
        {!isSectionCard && !isWelcomeGate && (
          <TourMascot
            stepId={mascotStepId}
            language={language}
            label={title}
            volume={volume}
            onVolumeChange={onVolumeChange}
            variant="hero"
          />
        )}

        <div className={`flex flex-col flex-1 min-w-0 justify-between gap-3`}>
          <div className={`min-w-0 ${isSectionCard ? `flex w-full items-start gap-3 justify-between ${isHebrew ? 'flex-row-reverse' : ''}` : ''}`}>
            <div className="min-w-0 flex-1">
              <h2
                className={`${
                  showWelcome || isSectionCard
                    ? 'text-xl sm:text-2xl font-semibold tracking-tight'
                    : 'text-base sm:text-lg font-bold'
                } ${textPrimary} leading-snug`}
              >
                {title}
              </h2>
              {showWelcome ? (
                <div className={`mt-3 space-y-2.5 text-sm sm:text-[15px] ${textSecondary} leading-relaxed`}>
                  {welcomeParagraphs.map((p, i) => (
                    <p key={i}>
                      {p}
                    </p>
                  ))}
                </div>
              ) : (
                renderBody(
                  description,
                  `mt-1.5 ${isSectionCard ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} ${textSecondary} leading-relaxed`
                )
              )}
              {proTip && (
                <div className={`mt-1.5 text-[10px] sm:text-xs font-medium text-forest-ondark dark:text-forest leading-snug flex items-start gap-1.5`}>
                  <Lightbulb className="shrink-0 mt-0.5" size={12} aria-hidden />
                  <div className="min-w-0 flex-1 space-y-1">
                    {bodyLines(proTip).map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </div>
              )}
              {showMissingTargetFallback && (
                <p className="mt-1.5 text-[10px] sm:text-xs font-semibold text-forest-ondark dark:text-forest">
                  {isHebrew ? 'האלמנט לא נמצא — המשיכו עם הבא' : 'Target not found — press Next to continue'}
                </p>
              )}
            </div>
            {isSectionCard && (
              <div
                className={`shrink-0 flex items-center gap-1.5 pt-1 ${isHebrew ? 'flex-row-reverse' : ''}`}
              >
                <span
                  className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                    isDarkMode
                      ? 'bg-forest/15 text-forest ring-1 ring-forest/30'
                      : 'bg-forest-faint text-forest-ondark ring-1 ring-forest'
                  }`}
                  aria-hidden
                >
                  <SectionIcon size={13} strokeWidth={2.25} />
                </span>
                {sectionProgressLabel && (
                  <span
                    className={`text-xs font-semibold tabular-nums whitespace-nowrap ${
                      isDarkMode ? 'text-forest' : 'text-forest-ondark'
                    }`}
                  >
                    {sectionProgressLabel}
                  </span>
                )}
              </div>
            )}
          </div>
          {showWelcome && (
            <div className="pt-2 w-full">
              <button
                type="button"
                onClick={() => {
                  cancelSkipArm();
                  primaryHandler?.();
                }}
                className={`${primaryBtnClass} w-full px-5 py-2.5`}
              >
                {welcomeCta || (isHebrew ? 'התחלת הסיור' : 'Start tour')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const panelBlends = panelPlacement?.blend || step?.panelBlend;
  const panelCompact = step?.panelCompact || panelPlacement?.layout === 'below-highlight-compact';
  const isSidePanel = step?.panelDock === 'side' || panelPlacement?.layout === 'side';
  // Orange ring only after a misclick on this step — cleared on step change / timeout.
  const misclickCardClass = misclickPulse
    ? (misclickPulse % 2 === 0 ? 'playbook-misclick-card' : 'playbook-misclick-card-alt')
    : '';
  const spotlightCard = (
    <div
      ref={panelRef}
      className={`fixed rounded-lg pointer-events-auto z-[10002] overflow-hidden ${
        isSidePanel
          ? 'max-h-[min(78vh,560px)] overflow-y-auto'
          : 'max-h-[min(85vh,520px)]'
      } ${
        panelBlends
          ? `${isDarkMode ? 'bg-canvas/92' : 'bg-white/92'}  border border-forest/35 `
          : `${cardBg}  border-2 border-forest/50`
      } ${misclickCardClass}`}
      style={undefined}
    >
      <div
        className={`${
          isSidePanel
            ? `flex flex-col gap-2.5 p-3 ${isHebrew ? 'text-right' : 'text-left'}`
            : `flex ${panelCompact ? 'gap-2 p-2.5' : 'gap-3 sm:gap-4 p-3 sm:p-4'} items-stretch ${isHebrew ? 'flex-row-reverse' : ''}`
        }`}
      >
        <TourMascot
          stepId={mascotStepId}
          language={language}
          label={title}
          volume={volume}
          onVolumeChange={onVolumeChange}
          variant={isSidePanel ? 'stack' : (step?.mascotVariant || 'horizontal')}
        />

        <div className={`flex flex-col flex-1 min-w-0 justify-between gap-2 ${isHebrew ? 'text-right' : 'text-left'}`}>
          <div className="min-w-0">
            <h2 className={`${isSidePanel || panelCompact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold ${textPrimary} leading-snug`}>
              {title}
            </h2>
            {renderBody(
              description,
              `mt-0.5 ${isSidePanel || panelCompact ? 'text-[10px] sm:text-[11px]' : 'text-[11px] sm:text-xs'} ${textSecondary} leading-snug`
            )}
            {proTip && (
              <div className={`mt-1 text-[10px] font-medium text-forest-ondark dark:text-forest flex items-start gap-1.5`}>
                <Lightbulb className="shrink-0 mt-0.5" size={11} aria-hidden />
                <div className="min-w-0 flex-1 space-y-1">
                  {bodyLines(proTip).map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            )}
            {stepHint}
          </div>
        </div>
      </div>
    </div>
  );

  const loopPhases = Array.isArray(step?.loopPhases) ? step.loopPhases : [];

  const floatStoryLayout = (
    <div className="absolute inset-0 z-[10001] pointer-events-none overflow-hidden px-[clamp(0.75rem,2vw,2.5rem)] pt-[clamp(0.5rem,1.5vh,1rem)] pb-[5.75rem]">
      <div
        className={`h-full w-full max-w-[1500px] mx-auto flex items-stretch gap-[clamp(0.75rem,2.5vw,2.5rem)] overflow-hidden ${
          isHebrew ? 'flex-row-reverse' : ''
        }`}
      >
        {/* Video scales with vmin so it stays proportional at any Chrome zoom */}
        <div className="pointer-events-auto w-[clamp(14rem,30vmin,22rem)] shrink-0 flex items-center self-center">
          <div className={`${cardBg} w-full rounded-lg  border border-forest/40 p-[clamp(0.4rem,1vh,0.75rem)]`}>
            <TourMascot
              stepId={mascotStepId}
              language={language}
              label={title}
              volume={volume}
              onVolumeChange={onVolumeChange}
              variant="heroLarge"
            />
          </div>
        </div>

        {/* Document column: header + equal-height loop rows that share remaining vh */}
        <article
          className={`flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col ${
            isHebrew ? 'text-right' : 'text-left'
          }`}
        >
          <header className="border-b border-white/20 pb-[clamp(0.5rem,1.5vh,1rem)] mb-[clamp(0.5rem,1.5vh,1rem)] shrink-0">
            <div className={`flex items-center gap-[clamp(0.4rem,1vw,0.75rem)] mb-[clamp(0.4rem,1vh,0.75rem)] ${isHebrew ? 'flex-row-reverse' : ''}`}>
              <span className="inline-flex h-[clamp(2rem,4.5vh,2.75rem)] w-[clamp(2rem,4.5vh,2.75rem)] items-center justify-center rounded-full bg-forest/20 ring-1 ring-forest/50 text-forest shrink-0">
                <RefreshCw size={18} className="animate-[spin_8s_linear_infinite]" aria-hidden />
              </span>
            </div>
            <h2 className="text-[clamp(1.75rem,5.5vh,3rem)] font-semibold tracking-tight text-white drop-shadow-lg leading-[1.12]">
              {title}
            </h2>
            {renderBody(
              description,
              'mt-[clamp(0.4rem,1.2vh,0.75rem)] text-[clamp(1rem,2.4vh,1.35rem)] text-ink/90 drop-shadow-md leading-snug max-w-2xl'
            )}
          </header>

          {loopPhases.length > 0 && (
            <ol className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {loopPhases.map((item, i) => (
                <li
                  key={`${item.label}-${i}`}
                  className={`flex-1 min-h-0 border-b border-white/10 grid gap-[clamp(0.5rem,1.5vw,1rem)] items-center ${
                    isHebrew
                      ? 'grid-cols-[1fr_clamp(2rem,4vh,3rem)]'
                      : 'grid-cols-[clamp(2rem,4vh,3rem)_1fr]'
                  }`}
                >
                  {isHebrew ? (
                    <>
                      <div className="min-w-0">
                        <p className="text-[clamp(0.7rem,1.7vh,0.9rem)] font-bold uppercase tracking-[0.14em] text-forest">
                          {item.label}
                        </p>
                        <p className="mt-[0.15em] text-[clamp(0.95rem,2.2vh,1.2rem)] text-white/90 leading-snug">
                          {item.caption}
                        </p>
                      </div>
                      <span className="text-right text-[clamp(1.25rem,3.5vh,2rem)] font-semibold text-white/25 tabular-nums leading-none">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-[clamp(1.25rem,3.5vh,2rem)] font-semibold text-white/25 tabular-nums leading-none">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[clamp(0.7rem,1.7vh,0.9rem)] font-bold uppercase tracking-[0.14em] text-forest">
                          {item.label}
                        </p>
                        <p className="mt-[0.15em] text-[clamp(0.95rem,2.2vh,1.2rem)] text-white/90 leading-snug">
                          {item.caption}
                        </p>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
        </article>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-none"
      dir={direction}
      data-tour-overlay
      role="dialog"
      aria-modal="true"
      aria-label={title || (isHebrew ? 'סיור מודרך' : 'Guided tour')}
    >
      {Array.from({ length: REGION_SLOT_COUNT }, (_, index) => (
        <div
          key={index}
          ref={(el) => { regionRefs.current[index] = el; }}
          className={`absolute transition-opacity duration-300 ${
            softSpotlight ? 'bg-black/15' : 'bg-black/35 backdrop-blur-[0.5px]'
          } ${
            passThroughClicks ? 'pointer-events-none' : 'pointer-events-auto'
          }`}
          style={{ display: 'none' }}
          onClick={passThroughClicks ? undefined : (e) => {
            e.preventDefault();
            e.stopPropagation();
            signalMisclick();
          }}
        />
      ))}

      {isSpotlight && (
        <>
          {spotlightLabel && (
            <div
              ref={labelRef}
              className={`absolute z-[10002] px-3 py-1.5 rounded-full text-white text-xs sm:text-sm font-bold  pointer-events-none whitespace-nowrap ${
                softSpotlight ? 'bg-forest/75' : 'bg-forest-ondark'
              }`}
              style={{ display: 'none' }}
            >
              {spotlightLabel}
            </div>
          )}
          {secondaryHighlightedElement && (
            <div
              ref={secondaryRingRef}
              className={`absolute rounded-lg border-2 border-forest/40 bg-forest/10 pointer-events-none z-[10000] ${ringTransition}`}
              style={{ display: 'none' }}
            />
          )}
          <div
            ref={ringRef}
            className={`absolute rounded-lg pointer-events-none z-[10001] ${
              softSpotlight
                ? 'border-2 border-forest/45 bg-forest/5  '
                : isInteractive
                  ? 'border-2 border-forest/70  '
                  : 'border-2 border-forest/80  '
            } ${ringTransition}`}
            style={{ display: 'none' }}
          />
        </>
      )}

      {/* Click proxy: only for single-control interactive steps — never over
          scrollable menus (client list) or waitForEvent pickers. */}
      {useHitProxy && (
        <button
          type="button"
          ref={hitProxyRef}
          aria-label={clickLabel || spotlightLabel || 'Tour action'}
          className="absolute z-[10005] rounded-lg cursor-pointer pointer-events-auto bg-transparent border-0 p-0"
          style={{ display: 'none' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Tour-owned activate — do not depend on forwarding into a possibly
            // clipped / remounted dashboard button under the overlay.
            if (typeof onInteractiveActivate === 'function') {
              onInteractiveActivate();
              return;
            }
            if (highlightedElement?.isConnected && typeof highlightedElement.click === 'function') {
              highlightedElement.click();
            }
          }}
        />
      )}

      {bottomHotBar}

      {isFloatStory ? (
        floatStoryLayout
      ) : isCentered ? (
        <div
          key={showWelcome ? `welcome-${welcomeStage}` : (mascotStepId || stepIndex)}
          className="absolute z-[10001] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
          style={{
            width: isWelcomeVideo
              ? WELCOME_VIDEO_WIDTH
              : showWelcome
                ? 'min(92vw, 420px)'
                : CARD_WIDTH,
          }}
        >
          {(showWelcome || step) && (waitingForDashboard ? dashboardSkeletonCard : centeredCard)}
        </div>
      ) : (
        <div key={step?.id}>{isSpotlight && spotlightCard}</div>
      )}
    </div>
  );
};

export default TourOverlay;
